import MonetaryUtils from "../../../shared/utils/MonetaryUtils";
import CnpjUtils from "../../../shared/utils/CnpjUtils";

export const ASSESSMENT_DEFAULT_PAGE_SIZE = 10;

export const ASSESSMENT_STATUS_OPTIONS = [
	{
		label: "Apurado",
		value: "APURADO"
	},
	{
		label: "Aprovado",
		value: "APROVADO"
	},
	{
		label: "Reprovado",
		value: "REPROVADO"
	}
];

const appendParam = (params, key, value) => {
	if (value !== undefined && value !== null && value !== "") {
		params.append(key, value);
	}
};

const formatStatusParam = status => {
	if (!status) {
		return "";
	}

	if (Array.isArray(status)) {
		return status.filter(Boolean).join(",");
	}

	return status;
};

const getBooleanFilterValue = ({
	positive,
	negative
}) => {
	if (positive && !negative) {
		return true;
	}

	if (!positive && negative) {
		return false;
	}

	return "";
};

const parseCurrencyFilterValue = value => {
	if (value === undefined || value === null || value === "") {
		return "";
	}

	return MonetaryUtils.parseCurrency(value);
};

const parsePercentageFilterValue = value => {
	if (value === undefined || value === null || value === "") {
		return "";
	}

	return MonetaryUtils.parseDecimal(
		String(value).replace("%", "")
	);
};

const cleanDocumentFilterValue = value => {
	if (!value) {
		return "";
	}

	return CnpjUtils.cleanAlphanumericCnpj
		? CnpjUtils.cleanAlphanumericCnpj(value)
		: String(value).replace(/[^\dA-Za-z]/g, "");
};

export const formatAssessmentFilters = (filters = {}) => ({
	cycleId: filters.cycleId,
	status: filters.status,
	de: String(filters.period?.from ?? "").trim(),
	ate: String(filters.period?.to ?? "").trim(),
	metaNetSalesAtingida: getBooleanFilterValue({
		positive: filters.netSalesGoalReached,
		negative: filters.netSalesGoalNotReached
	}),
	metaVariluxAtingida: getBooleanFilterValue({
		positive: filters.variluxGoalReachedOrNotApplicable,
		negative: filters.variluxGoalNotReached
	}),
	metaGrupoAtingida: getBooleanFilterValue({
		positive: filters.groupGoalReachedOrNotApplicable,
		negative: filters.groupGoalNotReached
	}),
	inadimplente: getBooleanFilterValue({
		positive: filters.latePaymentYes,
		negative: filters.latePaymentNo
	}),
	nomeEquipamento: filters.equipmentName,
	valorEquipamentoDe: parseCurrencyFilterValue(filters.equipmentValue?.from),
	valorEquipamentoAte: parseCurrencyFilterValue(filters.equipmentValue?.to),
	percentualDescontoDe: parsePercentageFilterValue(filters.grantedDiscountPercentage?.from),
	percentualDescontoAte: parsePercentageFilterValue(filters.grantedDiscountPercentage?.to),
	cnpj: cleanDocumentFilterValue(filters.clientDocument),
	tamanhoCliente: filters.clientSize
});

export const buildAssessmentsParams = ({
	cycleId,
	status,
	filters = {},
	page = 0,
	size = ASSESSMENT_DEFAULT_PAGE_SIZE,
	sort
} = {}) => {
	const params = new URLSearchParams();

	const formattedFilters = formatAssessmentFilters({
		...filters,
		cycleId: cycleId ?? filters.cycleId,
		status: status ?? filters.status
	});

	appendParam(params, "cicloId", formattedFilters.cycleId);
	appendParam(params, "status", formatStatusParam(formattedFilters.status));
	appendParam(params, "de", formattedFilters.de);
	appendParam(params, "ate", formattedFilters.ate);
	appendParam(params, "metaNetSalesAtingida", formattedFilters.metaNetSalesAtingida);
	appendParam(params, "metaVariluxAtingida", formattedFilters.metaVariluxAtingida);
	appendParam(params, "metaGrupoAtingida", formattedFilters.metaGrupoAtingida);
	appendParam(params, "inadimplente", formattedFilters.inadimplente);
	appendParam(params, "nomeEquipamento", formattedFilters.nomeEquipamento);
	appendParam(params, "valorEquipamentoDe", formattedFilters.valorEquipamentoDe);
	appendParam(params, "valorEquipamentoAte", formattedFilters.valorEquipamentoAte);
	appendParam(params, "percentualDescontoDe", formattedFilters.percentualDescontoDe);
	appendParam(params, "percentualDescontoAte", formattedFilters.percentualDescontoAte);
	appendParam(params, "cnpj", formattedFilters.cnpj);
	appendParam(params, "tamanhoCliente", formattedFilters.tamanhoCliente);
	appendParam(params, "pagina", page);
	appendParam(params, "tamanho", size);
	appendParam(params, "sort", sort);

	return params;
};

export const buildAssessmentsExportParams = ({
	cycleId,
	status,
	filters = {},
	sort
} = {}) => {
	const params = new URLSearchParams();

	const formattedFilters = formatAssessmentFilters({
		...filters,
		cycleId: cycleId ?? filters.cycleId,
		status: status ?? filters.status
	});

	appendParam(params, "cicloId", formattedFilters.cycleId);
	appendParam(params, "status", formatStatusParam(formattedFilters.status));
	appendParam(params, "de", formattedFilters.de);
	appendParam(params, "ate", formattedFilters.ate);
	appendParam(params, "metaNetSalesAtingida", formattedFilters.metaNetSalesAtingida);
	appendParam(params, "metaVariluxAtingida", formattedFilters.metaVariluxAtingida);
	appendParam(params, "metaGrupoAtingida", formattedFilters.metaGrupoAtingida);
	appendParam(params, "inadimplente", formattedFilters.inadimplente);
	appendParam(params, "nomeEquipamento", formattedFilters.nomeEquipamento);
	appendParam(params, "valorEquipamentoDe", formattedFilters.valorEquipamentoDe);
	appendParam(params, "valorEquipamentoAte", formattedFilters.valorEquipamentoAte);
	appendParam(params, "percentualDescontoDe", formattedFilters.percentualDescontoDe);
	appendParam(params, "percentualDescontoAte", formattedFilters.percentualDescontoAte);
	appendParam(params, "cnpj", formattedFilters.cnpj);
	appendParam(params, "tamanhoCliente", formattedFilters.tamanhoCliente);
	appendParam(params, "sort", sort);

	return params;
};

export const formatAssessmentsResponse = data => {
	const page = data.paginaAtual ?? 0;

	return {
		items: (data.itens || []).map((item, index) => ({
			rowKey: `${page}-${index}`,
			assessmentDate: item.dataApuracao,
			clientSize: item.tamanhoCliente,
			client: item.cliente,
			cnpj: item.cnpj,
			city: item.cidade,
			state: item.uf,
			equipment: item.equipamento,
			purchaseDate: item.dataCompra,
			equipmentSaleValue: item.valorVendaEquipamento,
			installmentsQuantity: item.quantidadeParcelas,
			discountInstallments: item.parcelasPraDescontar,
			baseline: item.baseline,
			netSales: item.netSales,
			netSalesGrowth: item.crescimentoNetSales,
			baselineVx: item.baselineVx,
			personalized: item.personalizado,
			baselineVxPers:
				`${item.baselineVx ?? "-"} / ${item.personalizado ?? "-"}`,
			variluxResult: item.variluxPersoResultado,
			rulesGoals: Array.isArray(item.metasRegras)
				? item.metasRegras.map((rule, index) => ({
					id: index + 1,
					goal: rule.meta,
					netSales: rule.valorNetSales,
					discountPercentage: rule.percentualDesconto
				}))
				: [],
			netSalesGoal: item.metaNetSales,
			variluxGoal: item.metaVarilux,
			groupGoal: item.metaGrupo,
			latePayment: item.inadimplencia,
			grantedDiscountPercentage: item.percentualDescontoConcedido,
			originalInstallmentValue: item.valorParcelaOriginal,
			installmentDiscountValue: item.valorDescontoParcela,
			installmentValueWithDiscount: item.valorParcelaComDesconto,
			status: item.status,
			approvalDate: item.dataAprovacao,
			approved: item.aprovado,
			appliedOracle: item.aplicadoOracle,
			appliedOracleDate: item.dataAplicacaoOracle,
			raw: item
		})),
		pagination: {
			totalElements: data.totalElementos ?? 0,
			totalPages: data.totalPaginas ?? 0,
			page,
			size: data.tamanho ?? ASSESSMENT_DEFAULT_PAGE_SIZE
		},
		statistics: data.estatisticas ?? null
	};
};

export const formatOngoingAssessmentCyclesResponse = cycles => {
	return cycles.map(cycle => {
		const discountMonths = (cycle.mesesDesconto || []).map(month => ({
			number: month.numero,
			name: month.nome,
			abbreviation: month.abreviacao
		}));

		return {
			cycleId: cycle.cicloId,
			startYear: cycle.anoInicio,
			startMonth: cycle.mesInicio,
			endYear: cycle.anoFim,
			endMonth: cycle.mesFim,
			status: cycle.status,
			startDate: cycle.dataInicio,
			discountMonths,
			discountMonthsLabel: discountMonths
				.map(month => month.abbreviation)
				.join(" / "),
			raw: cycle
		};
	});
};

export const formatAssessmentDecisionResponse = data => ({
	cycleId: data.cicloId,
	status: data.status,
	decidedAssessmentsCount: data.apuracoesDecididas,
	decisionDate: data.dataDecisao,
	notificationEmailStatus: data.emailNotificacao,
	oracleApplicationStatus: data.aplicacaoOracle,
	raw: data
});

export const validateAssessmentPeriod = (period = {}) => {
	const from = String(period.from ?? "").trim();
	const to = String(period.to ?? "").trim();
	const monthYearPattern = /^(0[1-9]|1[0-2])\/\d{4}$/;

	if (
		(from && !monthYearPattern.test(from)) ||
		(to && !monthYearPattern.test(to))
	) {
		return "Informe o período no formato MM/AAAA, com mês entre 01 e 12.";
	}

	if (from && to) {
		const [fromMonth, fromYear] = from.split("/").map(Number);
		const [toMonth, toYear] = to.split("/").map(Number);

		const initialPeriod = fromYear * 12 + fromMonth;
		const finalPeriod = toYear * 12 + toMonth;

		if (finalPeriod < initialPeriod) {
			return "O período final não pode ser anterior ao inicial.";
		}
	}

	return "";
};

const isInvalidFilterRange = (range, parseValue) => {
	const from = range?.from;
	const to = range?.to;

	const hasBothValues =
		String(from ?? "").trim() !== "" &&
		String(to ?? "").trim() !== "";

	return hasBothValues && parseValue(from) > parseValue(to);
};

export const validateAssessmentFilters = (
	filters = {},
	{ includePeriod = false } = {}
) => {
	const errors = {};

	if (
		isInvalidFilterRange(
			filters.equipmentValue,
			parseCurrencyFilterValue
		)
	) {
		errors.equipmentValue =
			"Valor do equipamento: o valor inicial deve ser menor ou igual ao final.";
	}

	if (
		isInvalidFilterRange(
			filters.grantedDiscountPercentage,
			parsePercentageFilterValue
		)
	) {
		errors.grantedDiscountPercentage =
			"Desconto concedido: o valor inicial deve ser menor ou igual ao final.";
	}

	if (includePeriod) {
		const periodError = validateAssessmentPeriod(filters.period);

		if (periodError) {
			errors.period = periodError;
		}
	}

	const clientDocument = String(filters.clientDocument ?? "").trim();

	if (
		clientDocument &&
		!CnpjUtils.isCompleteAlphanumericCnpj(clientDocument)
	) {
		errors.clientDocument =
			"Informe o CNPJ completo, com 14 caracteres e os dois últimos numéricos.";
	}

	return errors;
};